import { Controller, FieldValues, get, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { cn } from '@/lib/utils'
import { DateRangePicker } from '@/components/ui/date-picker'
import { FormFieldWrapper } from './form-field-wrapper'
import type { FormFieldDateRangePickerProps } from './types'

/** Separate component for daterangepicker with 2 names */
export function FormFieldDateRange<T extends FieldValues>(
    props: FormFieldDateRangePickerProps<T>
) {
    const { t } = useTranslation()
    const {
        control,
        name,
        label,
        required,
        disabled,
        helperText,
        className,
        minDate,
        maxDate,
        showPresets,
        numberOfMonths,
        placeholder,
        endDatePresets,
        clearable,
        onValueChange,
    } = props

    const [fromName, toName] = name
    const fromValue = useWatch({ control, name: fromName })
    const toValue = useWatch({ control, name: toName })

    return (
        <Controller
            control={control}
            name={fromName}
            render={({ field: fromField, formState }) => (
                <Controller
                    control={control}
                    name={toName}
                    render={({ field: toField }) => {
                        const rawError =
                            get(formState.errors, fromName)?.message ||
                            get(formState.errors, toName)?.message
                        const error = rawError
                            ? t(rawError as string)
                            : undefined
                        const hasError = !!error

                        // Parse safely — guard against invalid date strings
                        const parseDate = (v: unknown): Date | undefined => {
                            if (!v) return undefined
                            // Handle array value (e.g. from URL filter reload: ["from", "to"])
                            if (Array.isArray(v))
                                return v[0] ? new Date(v[0]) : undefined
                            const d = new Date(v as string | number | Date)
                            return isNaN(d.getTime()) ? undefined : d
                        }

                        // fromValue may be [from, to] array from URL filter reload
                        const isArrayValue = Array.isArray(fromValue)
                        const parsedFrom = parseDate(
                            isArrayValue ? fromValue[0] : fromValue
                        )
                        const parsedTo = parseDate(
                            isArrayValue ? fromValue[1] : toValue
                        )
                        const value = parsedFrom
                            ? { from: parsedFrom, to: parsedTo }
                            : undefined

                        const handleChange = (
                            range: { from: Date; to?: Date } | undefined
                        ) => {
                            if (range?.from !== undefined) {
                                fromField.onChange(
                                    dayjs(range.from).format('YYYY-MM-DD')
                                )
                            }
                            if (range?.to !== undefined) {
                                toField.onChange(
                                    dayjs(range.to).format('YYYY-MM-DD')
                                )
                            } else if (range === undefined) {
                                fromField.onChange(null)
                                toField.onChange(null)
                            }
                            // Fire onValueChange when both dates are selected or when cleared
                            if (range?.from && range?.to) {
                                onValueChange?.([
                                    dayjs(range.from).format('YYYY-MM-DD'),
                                    dayjs(range.to).format('YYYY-MM-DD'),
                                ])
                            } else if (range === undefined) {
                                onValueChange?.(['', ''])
                            }
                        }

                        return (
                            <FormFieldWrapper
                                label={label}
                                required={required}
                                error={error}
                                helperText={helperText}
                                className={className}
                            >
                                <DateRangePicker
                                    value={value}
                                    onChange={handleChange}
                                    placeholder={placeholder}
                                    disabled={disabled}
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    showPresets={showPresets}
                                    numberOfMonths={numberOfMonths}
                                    endDatePresets={endDatePresets}
                                    clearable={clearable}
                                    className={cn(
                                        hasError &&
                                            'border-destructive hover:border-destructive hover:bg-background focus-visible:ring-destructive/30'
                                    )}
                                />
                            </FormFieldWrapper>
                        )
                    }}
                />
            )}
        />
    )
}
